"use client";

import { Metadata } from 'next';
import { DemoModal } from "@/components/shared/DemoModal";
import { Link } from "@/i18n/navigation";
import { CustomButton } from "@/components/ui/custom-button";
import { motion } from 'framer-motion';
import { useTranslations } from "next-intl";

function ContactUs() {
  const t = useTranslations();

  return (
    <main className="flex min-h-screen flex-col justify-center bg-white" role="main" aria-label="Contact Us">
      <div className="mx-auto max-w-6xl px-4 py-8 lg:px-8 md:py-16 sm:px-6 sm:py-12 xl:max-w-7xl">
        {/* Header Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="pt-4 text-center lg:pt-10 md:pt-8 sm:pt-6"
          aria-label="Contact us introduction"
        >
          <h1 className="text-3xl font-medium text-gray-900 lg:text-6xl md:text-5xl sm:text-4xl">
            Get in touch
          </h1>
          <p className="mx-auto mt-3 max-w-lg text-sm text-gray-700 lg:text-xl md:mt-6 md:text-lg sm:mt-4 sm:text-base">
            We&apos;re here to help you. Reach out to our team for support, demos, or just to say hello.
          </p>
        </motion.section>

        {/* Contact Information */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3 md:mt-16 md:grid-cols-2 sm:mt-12 sm:gap-8 xl:mt-20"
          role="region"
          aria-label="Contact options"
        >
          <h2 className="sr-only">Contact Methods</h2>

          <ul className="contents" role="list" aria-label="Contact methods">
            {/* Email */}
            <li className="list-none">
              <article className="group flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition-all duration-300 hover:shadow-md sm:p-8" role="article" aria-label="Email support contact method">
                <div className="mb-4 flex size-10 items-center justify-center rounded-xl bg-gray-100 transition-colors duration-300 group-hover:bg-gray-900 sm:mb-6 sm:size-12">
                  <svg className="size-6 text-gray-700 transition-colors duration-300 group-hover:text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true">
                    <path strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="mb-2 text-lg font-semibold text-gray-900 sm:mb-3 sm:text-xl">Email Support</h3>
                <p className="mb-3 grow text-sm text-gray-700 sm:mb-4 sm:text-base">Get help with technical issues, account questions, or general inquiries.</p>
                <a
                  href="mailto:support@studystash.com"
                  className="inline-block"
                  aria-label="Email Support: Send email to support@studystash.com"
                >
                  <CustomButton
                    variant="outline"
                    size="sm"
                    aria-label="Email Support"
                  >
                    Email Support
                  </CustomButton>
                </a>
              </article>
            </li>

            {/* Demo */}
            <li className="list-none">
              <article className="group flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition-all duration-300 hover:shadow-md sm:p-8" role="article" aria-label="Watch demo contact method">
                <div className="mb-4 flex size-10 items-center justify-center rounded-xl bg-gray-100 transition-colors duration-300 group-hover:bg-gray-900 sm:mb-6 sm:size-12">
                  <svg className="size-6 text-gray-700 transition-colors duration-300 group-hover:text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true">
                    <path strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="mb-2 text-lg font-semibold text-gray-900 sm:mb-3 sm:text-xl">Watch the Demo</h3>
                <p className="mb-3 grow text-sm text-gray-700 sm:mb-4 sm:text-base">Watch our product demonstration to see StudyStash in action.</p>
                <DemoModal type="sm" />
              </article>
            </li>

            {/* Book Demo */}
            <li className="list-none">
              <article className="group flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition-all duration-300 hover:shadow-md sm:p-8" role="article" aria-label="Book demo contact method">
                <div className="mb-4 flex size-10 items-center justify-center rounded-xl bg-gray-100 transition-colors duration-300 group-hover:bg-gray-900 sm:mb-6 sm:size-12">
                  <svg className="size-6 text-gray-700 transition-colors duration-300 group-hover:text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true">
                    <path strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="mb-2 text-lg font-semibold text-gray-900 sm:mb-3 sm:text-xl">Book a Demo</h3>
                <p className="mb-3 grow text-sm text-gray-700 sm:mb-4 sm:text-base">Schedule a call with our team to discuss how we can support you.</p>
                <Link
                  href="/book-a-demo"
                  className="inline-block"
                  aria-label="Schedule a demo with our team: Schedule Demo"
                >
                  <CustomButton
                    variant="outline"
                    size="sm"
                    aria-label="Schedule Demo"
                  >
                    Schedule Demo
                  </CustomButton>
                </Link>
              </article>
            </li>
          </ul>
        </motion.section>

        {/* Company Information */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-16 border-t border-gray-200 pt-6 lg:mt-36 lg:pt-12 md:mt-32 md:pt-10 sm:mt-24 sm:pt-8 xl:mt-40 xl:pt-16"
          role="region"
          aria-label="Company information"
        >
          <div className="text-center">
            <h2 className="mb-2 text-base font-semibold text-gray-900 sm:mb-3 sm:text-lg">{t("general.entity")}</h2>
            <address className="space-y-1 text-xs text-gray-700 sm:text-sm not-italic">
              <p className="font-medium">{t("general.address")}</p>
            </address>
            <p className="mt-2 text-xs text-gray-600 sm:mt-3">
              {t("general.registration")}
            </p>
          </div>
        </motion.section>
      </div>
    </main>
  );
}

export default ContactUs;
